import React, { useState, useEffect, useRef, useMemo } from 'react';
import debounce from 'just-debounce-it';
import { Popover, createStyles } from '@mantine/core';
import ClassicLoadingSpinner from './LoadingSpinner/ClassicLoadingSpinner';
import { useSearchItemLocationsLazyQuery } from '../graphql/queries/searchItemLocations.generated';
import { ItemLocationDetailFragment } from '../graphql/fragments/itemLocationDetail.generated';
import i18n from '@/utils/i18n';

const SEARCH_RESULT_MAX = 5;

const useStyles = createStyles((theme) => ({
  option: {
    cursor: 'pointer',
    padding: '0.5rem',
    fontSize: '0.8rem',
    borderBottom: '1px solid #eee',
    backgroundColor: '#fff',

    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
}));

const ItemLocationSearch = (props: {
  setLocationInfo: (id: string, itemLocationName: string) => void;
}) => {
  const { classes } = useStyles();
  const [text, setText] = useState('');
  const [options, setOptions] = useState<ItemLocationDetailFragment[]>([]);
  const [search, { loading }] = useSearchItemLocationsLazyQuery();
  const inputRef = useRef<any>(null);
  const [dropdownWidth, setDropdownWidth] = useState('auto');
  const [selectedOption, setSelectedOption] =
    useState<ItemLocationDetailFragment | null>(null);

  const fetchResults = useMemo(
    () =>
      debounce(async (text: string) => {
        if (!text || text.length < 3 || text === selectedOption?.name) return;

        const { data } = await search({
          fetchPolicy: 'no-cache',
          variables: { search_term: text },
        });

        if (data) {
          setOptions(data.searchItemLocations.slice(0, SEARCH_RESULT_MAX));
        }
      }, 500),
    [search, selectedOption],
  );

  useEffect(() => {
    fetchResults.cancel();
    if (text) {
      fetchResults(text);
    }
  }, [text, fetchResults]);

  useEffect(() => {
    if (inputRef.current != null) {
      setDropdownWidth(`${inputRef.current.offsetWidth}px`);
    }
  }, [inputRef]);

  if (selectedOption != null) {
    props.setLocationInfo(selectedOption.id, selectedOption.name);
  }

  return (
    <div>
      <Popover
        opened={options.length > 0 || loading}
        position="bottom"
        withArrow={false}
      >
        <Popover.Target>
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={i18n.t('ItemLocation:searchPlaceholder')}
          />
        </Popover.Target>
        <Popover.Dropdown
          style={{
            overflowY: 'auto',
            maxHeight: '4rem',
            border: '1px solid #ccc',
            padding: '0',
            zIndex: 100,
          }}
        >
          {loading && (
            <div
              style={{
                width: dropdownWidth,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <ClassicLoadingSpinner />
            </div>
          )}

          {!loading &&
            options.map((opt, index) => (
              <option
                className={classes.option}
                style={{
                  width: dropdownWidth,
                  borderBottom:
                    index === options.length - 1 ? 'none' : '1px solid #eee',
                }}
                onClick={() => {
                  setOptions([]);
                  setSelectedOption(opt);
                  setText(opt.name);
                }}
                key={opt.id}
                value={opt.id}
                label={opt.name}
              />
            ))}
        </Popover.Dropdown>
      </Popover>
    </div>
  );
};

export default ItemLocationSearch;
